import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { ScrollToTop } from "./components/ScrollToTop";
import RouteChangeTracker from "./components/RouteChangeTracker";
import { captureUTMParams } from "./utils/utmTracking";
import { Home } from "./pages/Home";
import { About } from "./pages/About";
import { History } from "./pages/History";
import { PompeoCoppini } from "./pages/PompeoCoppini";
import { WaldineTauch } from "./pages/WaldineTauch";
import { FiestaMedal } from "./pages/FiestaMedal";
import { AdultClasses } from "./pages/AdultClasses";
import { KidsClasses } from "./pages/KidsClasses";
import { OpenSessions } from "./pages/OpenSessions";
import { Instructors } from "./pages/Instructors";
import { Calendar } from "./pages/Calendar";
import { Workshops } from "./pages/Workshops";
import { Register } from "./pages/Register";
import { RegisterOpenSession } from "./pages/RegisterOpenSession";
import { RegisterClass } from "./pages/RegisterClass";
import { NotFound } from "./pages/NotFound";
import { Membership } from "./pages/Membership";
import { Support } from "./pages/Support";
import { Contact } from "./pages/Contact";
import BecomeAModel from "./pages/BecomeAModel";
import { MemberLogin } from "./pages/MemberLogin";
import { MemberDirectory } from "./pages/MemberDirectory";
import { MemberProfile } from "./pages/MemberProfile";
import { MemberHistory } from "./pages/MemberHistory";
import { AdminMembers } from "./pages/AdminMembers";
import { AdminRegistrations } from "./pages/AdminRegistrations";
import { AdminContent } from "./pages/AdminContent";
import { AdminWorkshops } from "./pages/AdminWorkshops";
import { AdminMemberEdit } from "./pages/AdminMemberEdit";
import AdminSubscribers from "./pages/AdminSubscribers";
import AdminMedia from "./pages/AdminMedia";
import AdminRecoverArtwork from "./pages/AdminRecoverArtwork";
import { AdminArtistOfMonth } from "./pages/AdminArtistOfMonth";
import AdminContactAnalytics from "./pages/AdminContactAnalytics";
import { AdminKidsClasses } from "./pages/AdminKidsClasses";
import { AdminExhibitions } from "./pages/AdminExhibitions";
import AdminDashboard from "./pages/AdminDashboard";
import { MemberSubmissions } from "./pages/MemberSubmissions";
import { SubmissionForm } from "./pages/SubmissionForm";
import { JudgePortal } from "./pages/JudgePortal";
import { AdminJudgePreview } from "./pages/AdminJudgePreview";
import { Exhibitions } from "./pages/Exhibitions";
import { ExhibitionDetail } from "./pages/ExhibitionDetail";
import RadioWelcome from "./pages/RadioWelcome";
import { VerifyEmail } from "./pages/VerifyEmail";
import { SetPassword } from "./pages/SetPassword";
import { Toaster } from "./components/ui/sonner";

// Password Reset & Setup Pages
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import SetupPassword from "./pages/SetupPassword";

// SEO Landing Pages
import PaintingClassesSanAntonio from "./pages/PaintingClassesSanAntonio";
import DrawingClassesSanAntonio from "./pages/DrawingClassesSanAntonio";
import ArtSchoolSanAntonio from "./pages/ArtSchoolSanAntonio";
import ClassicalArtTraining from "./pages/ClassicalArtTraining";
import YouthArtClassesSanAntonio from "./pages/YouthArtClassesSanAntonio";
import ArtWorkshopsSanAntonio from "./pages/ArtWorkshopsSanAntonio";

// Guest submissions
import GuestSubmissionForm from "./pages/GuestSubmissionForm";
import GuestManageSubmission from "./pages/GuestManageSubmission";
import TeacherRoster from "./pages/TeacherRoster";
import DiBarbieriGallery from "./pages/DiBarbieriGallery";
import MemberGallery from "./pages/MemberGallery";
import MemberHolidayShow from './pages/MemberHolidayShow';
import AdminGallery from "./pages/AdminGallery";

function App() {
  // Capture UTM parameters on initial page load
  // This runs once when the app initializes
  useEffect(() => {
    captureUTMParams();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <RouteChangeTracker />
          <Header />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/welcome" element={<RadioWelcome />} />
            <Route path="/about" element={<About />} />
            <Route path="/about/history" element={<History />} />
            <Route path="/about/pompeo-coppini" element={<PompeoCoppini />} />
            <Route path="/about/waldine-tauch" element={<WaldineTauch />} />
            <Route path="/about/fiesta-medal" element={<FiestaMedal />} />
            <Route path="/classes" element={<Navigate to="/classes/adult" replace />} />
            <Route path="/classes/adult" element={<AdultClasses />} />
            <Route path="/classes/kids" element={<KidsClasses />} />
            <Route path="/classes/open-sessions" element={<OpenSessions />} />
            <Route path="/classes/instructors" element={<Instructors />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/workshops" element={<Workshops />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-open-session" element={<RegisterOpenSession />} />
            <Route path="/register-class" element={<RegisterClass />} />
            <Route path="/membership" element={<Membership />} />
            
            {/* SEO Landing Pages */}
            <Route path="/painting-classes-san-antonio" element={<PaintingClassesSanAntonio />} />
            <Route path="/drawing-classes-san-antonio" element={<DrawingClassesSanAntonio />} />
            <Route path="/art-school-san-antonio" element={<ArtSchoolSanAntonio />} />
            <Route path="/classical-art-training" element={<ClassicalArtTraining />} />
            <Route path="/youth-art-classes-san-antonio" element={<YouthArtClassesSanAntonio />} />
            <Route path="/kids-art-classes-san-antonio" element={<YouthArtClassesSanAntonio />} />
            <Route path="/art-workshops-san-antonio" element={<ArtWorkshopsSanAntonio />} />
            
            <Route path="/members" element={<Navigate to="/members/directory" replace />} />
            <Route path="/members/login" element={<MemberLogin />} />
            <Route path="/login" element={<Navigate to="/members/login" replace />} />
            <Route path="/members/directory" element={<MemberDirectory />} />
            <Route path="/members/profile" element={<MemberProfile />} />
            <Route path="/members/history" element={<MemberHistory />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/members" element={<AdminMembers />} />
            <Route path="/admin/members/:memberId" element={<AdminMemberEdit />} />
            <Route path="/admin/registrations" element={<AdminRegistrations />} />
            <Route path="/admin/content" element={<AdminContent />} />
            <Route path="/admin/workshops" element={<AdminWorkshops />} />
            <Route path="/admin/subscribers" element={<AdminSubscribers />} />
            <Route path="/admin/artist-of-month" element={<AdminArtistOfMonth />} />
            <Route path="/admin/media" element={<AdminMedia />} />
            <Route path="/admin/recover-artwork" element={<AdminRecoverArtwork />} />
            <Route path="/admin/contact-analytics" element={<AdminContactAnalytics />} />
            <Route path="/admin/kids-classes" element={<AdminKidsClasses />} />
            <Route path="/admin/exhibitions" element={<AdminExhibitions />} />
            <Route path="/members/submissions" element={<MemberSubmissions />} />
            <Route path="/members/submissions/:callId" element={<SubmissionForm />} />
            <Route path="/judge/:exhibitionId" element={<JudgePortal />} />
            <Route path="/roster/:token" element={<TeacherRoster />} />
            <Route path="/admin/exhibitions/:exhibitionId/judge-preview" element={<AdminJudgePreview />} />
            <Route path="/exhibitions" element={<Exhibitions />} />
            <Route path="/exhibitions/:slug" element={<ExhibitionDetail />} />
            <Route path="/di-barbieri-gallery" element={<DiBarbieriGallery />} />
            <Route path="/members/holiday-show" element={<MemberHolidayShow />} />
            <Route path="/members/gallery" element={<MemberGallery />} />
            <Route path="/admin/gallery" element={<AdminGallery />} />
            <Route path="/submissions/guest/:callId" element={<GuestSubmissionForm />} />
            <Route path="/submissions/guest/manage/:submissionId" element={<GuestManageSubmission />} />
            <Route path="/support" element={<Support />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/contact/model" element={<BecomeAModel />} />
            <Route path="/become-a-model" element={<BecomeAModel />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/setup-password" element={<SetupPassword />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer />
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
